import { web, TransactionType } from '@klever/sdk-web';
import { formatKleverError } from './formatKleverError';

export interface KleverTxData {
  payload: any;
  // Per-contract metadata string. Optional — pure transfers don't need any
  // metadata, while SC calls usually carry their function@arg@arg... blob.
  metadata?: string;
  network?: string;
  kdaFee?: string;
  // Override the contract type. Defaults to SmartContract for SC calls;
  // pass TransactionType.Transfer for KDA transfers (e.g. megaphone fee).
  contractType?: number;
}

/**
 * Universal Klever transaction builder/signer/broadcaster.
 *
 * Sets the correct network provider, builds the unsigned transaction (passing
 * kdaFee so the wallet charges fees in KLYX rather than KLV), prompts the user
 * to sign, and broadcasts.  Returns the transaction hash.
 *
 * All wallet-signing flows (paywall, profile access, marketplace, spotlight)
 * should funnel through this helper instead of duplicating the logic.
 */
export async function buildSignBroadcast(txData: KleverTxData): Promise<string> {
  const expectedNetwork = txData.network || import.meta.env.VITE_KLEVER_NETWORK || 'testnet';
  const providerConfig =
    expectedNetwork === 'testnet'
      ? { api: 'https://api.testnet.klever.org', node: 'https://node.testnet.klever.org' }
      : { api: 'https://api.mainnet.klever.org', node: 'https://node.mainnet.klever.org' };

  web.setProvider(providerConfig);
  await web.initialize();

  // isKleverWebActive checks window.kleverWeb.address — wallet must be connected
  if (!web.isKleverWebActive()) {
    throw new Error('Klever wallet not found. Please open in the Klever app or install the extension.');
  }

  const contracts = [{
    payload: txData.payload,
    type: txData.contractType ?? TransactionType.SmartContract,
  }];
  // SDK expects an array of metadata strings parallel to contracts. Omit when
  // there's no metadata (e.g. a pure transfer) so we don't send `undefined`.
  const txDataArr: string[] = txData.metadata !== undefined ? [txData.metadata] : [];
  const options = txData.kdaFee ? { kdaFee: txData.kdaFee } : undefined;

  let unsigned: any;
  try {
    // Pass kdaFee option so the wallet charges fees in KLYX instead of KLV.
    unsigned = await web.buildTransaction(contracts, txDataArr, options);
  } catch (buildErr) {
    // formatKleverError maps common SDK / VM strings to user-facing
    // sentences. Unmapped errors pass through raw so we never hide
    // information — see comment in the helper.
    throw new Error(formatKleverError(buildErr, 'Build failed'));
  }
  if (!unsigned) throw new Error('Build returned empty transaction');

  let signed: any;
  try {
    signed = await web.signTransaction(unsigned);
  } catch (signErr) {
    throw new Error(formatKleverError(signErr, 'Sign failed'));
  }
  if (!signed) throw new Error('Transaction signing was cancelled');

  // Guard against mobile environments where signTransaction may return the
  // unsigned tx as-is without showing a prompt.  Klever signed transactions
  // always carry at least one entry in the Signature array.
  const sigs: any[] = signed?.Signature ?? signed?.signature ?? [];
  if (!sigs.length) {
    throw new Error('Transaction was not signed. Please complete signing in your Klever wallet.');
  }

  const broadcast = await web.broadcastTransactions([signed]);
  if (!broadcast?.data?.txsHashes?.length) {
    throw new Error('Transaction broadcast failed or was rejected');
  }
  return broadcast.data.txsHashes[0];
}
